/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import * as DT from './TTVRDataTypes';
import MatSDK from '@/engine/WasmModules/MatSDK';
import type * as MatSDKTypes from '@/engine/WasmModules/MatSDK';
import * as THREE from 'three';
import { emmanaged, emmanaged2 } from '@/engine/PlanningAlgorithm/MatSDKHelpers';
import {
  findSplineSectorPoint,
  getSplineDistanceToRayProjected,
  makeTransformation,
  sliceParams
} from '@/engine/PlanningAlgorithm/Helpers';
import { MakeFunctor } from '@/engine/DataFlow/Functor';
import type { _rayCast2ResultItem } from '../common/CardiacAlgorithmHelpers';
import {
  _catmullRomSpline,
  _makeCheckCriteria,
  _rayCast,
  _rayCast2,
  _rayCast2BreakKind,
  _sliceInside,
  areaMeasurementParams
} from '../common/CardiacAlgorithmHelpers';
import {
  CatmullRomCurve3Type,
  distancePointToPlane,
  getCommonPerpendicularLine,
  getPlaneLineIntersectionPoint,
  makePlaneCoordinateSystem,
  makeRaysJointLine,
  makeRotationMatrix
} from '@/engine/utils/geometryHelper';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { filterPointsInsideSpline2d, isPointInsideSpline2d } from '@/engine/utils/matSdkHelpers';

export * from '../common/CardiacAlgorithmHelpers';

export const findCommissureInitialVectorTTVR = MakeFunctor(
  'FT.findCommissureInitialVectorTTVR',
  (area: DT.AreaMeasurement, point: DT.Point): DT.Vector => {
    return new DT.Vector(point.value.clone().sub(area.center).cross(area.normal).negate().cross(area.normal));
  }
);

export const findCommissurePointTTVR = MakeFunctor(
  'FT.findCommissurePointTTVR',
  (spline: DT.Spline, initialVector: DT.Vector, rotationAngle: DT.NumberT): DT.Point => {
    if (!spline.value.isClosed) {
      throw new Error('Spline must be closed');
    }
    if (rotationAngle.value < 0 || rotationAngle.value > 2 * Math.PI) {
      throw new Error('Invalid angle value: must be [0, 2*PI]');
    }

    return new DT.Point(
      findSplineSectorPoint(
        spline.value.splinePts,
        spline.value.center,
        spline.value.normal,
        initialVector.value
          .clone()
          .transformDirection(makeRotationMatrix(spline.value.center, spline.value.normal, rotationAngle.value))
      )
    );
  }
);

export const getAvailableValveDevicesTTVR = MakeFunctor(
  'FT.getAvailableValveDevicesTTVR',
  (system: DT.CardiacSystem): DT.IdentifiersList => {
    return new DT.IdentifiersList(system.getAvailableDevicesNames(DT.TTVRValveDevice.DeviceType));
  }
);

export const getValveDeviceTTVR = MakeFunctor(
  'FT.getValveDeviceTTVR',
  (system: DT.CardiacSystem, device: DT.StringT): DT.TTVRValveDevice => {
    return system.getDeviceAs(DT.TTVRValveDevice, device.value);
  }
);

export const defaultValveSizeTTVR = MakeFunctor(
  'FT.defaultValveSizeTTVR',
  (system: DT.CardiacSystem, anatomicalPerimeter: DT.NumberT, desiredOversize: DT.NumberT): DT.StringT => {
    const sortedDevices = system
      .getAllDevicesAs(DT.TTVRValveDevice)
      .map((d) => {
        return {
          name: d.name,
          diam: d.metadata.diameter
        };
      })
      .sort((a, b) => a.diam - b.diam);
    if (sortedDevices.length == 0) {
      throw new Error('Devices are not found');
    }
    const anatomicalDiameter = anatomicalPerimeter.value / Math.PI;
    for (const device of sortedDevices) {
      const oversize = (device.diam - anatomicalDiameter) / anatomicalDiameter;
      if (oversize >= desiredOversize.value) {
        return new DT.StringT(device.name);
      }
    }
    return new DT.StringT(sortedDevices[sortedDevices.length - 1].name);
  }
);

export const defaultValvePositionTTVR = MakeFunctor(
  'FT.defaultValvePositionTTVR',
  (
    valveInsertionDepth: DT.NumberT,
    apShift: DT.NumberT,
    slShift: DT.NumberT,
    valveRotationAngleDeg: DT.NumberT,
    plane: DT.Plane,
    apDir: DT.Vector
  ): DT.TransformMatrix => {
    const vecZ = plane.value.planeEq.normal.clone().negate().normalize();
    const vecY = apDir.value.clone().cross(vecZ).negate().cross(vecZ).normalize();
    const vecX = new THREE.Vector3().crossVectors(vecY, vecZ).normalize();
    const origin = plane.value.origin
      .clone()
      .addScaledVector(vecX, -slShift.value)
      .addScaledVector(vecY, apShift.value)
      .addScaledVector(vecZ, -(valveInsertionDepth.value + 0.1));
    const zRotation = new THREE.Matrix4().makeRotationZ((-valveRotationAngleDeg.value * Math.PI) / 180);
    const cs = new THREE.Matrix4().makeBasis(vecX, vecY, vecZ).setPosition(origin).multiply(zRotation);
    return new DT.TransformMatrix(cs);
  }
);

export const valveBottomPlaneTTVR = MakeFunctor('FT.deviceBottomPlaneTTVR', (cs: DT.TransformMatrix): DT.Plane => {
  const o = new THREE.Vector3(0, 0, 0).applyMatrix4(cs.value);
  const n = new THREE.Vector3(0, 0, -1).transformDirection(cs.value);
  return new DT.Plane({
    origin: o,
    planeEq: new THREE.Plane().setFromNormalAndCoplanarPoint(n, o)
  });
});

export const valveDiameterTTVR = MakeFunctor('FT.valveDiameterTTVR', (valve: DT.TTVRValveDevice): DT.NumberT => {
  return new DT.NumberT(valve.metadata.diameter);
});

export const valveAnchorDiameterTTVR = MakeFunctor(
  'FT.valveAnchorDiameterTTVR',
  (valve: DT.TTVRValveDevice): DT.NumberT => {
    return new DT.NumberT(valve.metadata.anchorDiameter);
  }
);

export const calculateToDeviceDistancesTTVR = MakeFunctor(
  'FT.calculateToDeviceDistancesTTVR',
  (polyline: DT.MatLineSegments, device: DT.TTVRValveDevice, cs: DT.TransformMatrix): DT.MatDoublesVector => {
    const pt0 = new THREE.Vector3(0, 0, 0).applyMatrix4(cs.value);
    const pt1 = new THREE.Vector3(0, 0, 20).applyMatrix4(cs.value);
    const ln = new THREE.Line3(pt0, pt1);
    const ret = new MatSDK.VectorDouble();
    const dummy = new THREE.Vector3();
    const pairs = polyline.value.getRawSegments();
    if (pairs.length > 0) {
      ret.resize(pairs.length / 2, 0);
      const coords = polyline.value.getRawVertices();
      for (let i = 0, sz = pairs.length / 2; i < sz; ++i) {
        const j = i * 2;
        const k = pairs[j] * 3;
        const point = new THREE.Vector3().fromArray(coords, k);
        const res = ln.closestPointToPoint(point, true, dummy);
        ret.set(i, Math.max(point.distanceTo(res) - device.metadata.diameter / 2, 0));
      }
    }
    return new DT.MatDoublesVector(ret);
  }
);

export const polylineToDeviceSmallestDistanceTTVR = MakeFunctor(
  'FT.polylineToDeviceSmallestDistanceTTVR',
  (
    polyline: DT.MatLineSegments,
    device: DT.TTVRValveDevice,
    cs: DT.TransformMatrix,
    zVector: DT.Vector,
    yVector: DT.Vector
  ): DT.DistanceMeasurement => {
    const pt0 = new THREE.Vector3(0, 0, 0).applyMatrix4(cs.value);
    const pt1 = new THREE.Vector3(0, 0, 20).applyMatrix4(cs.value);
    const ln = new THREE.Line3(pt0, pt1);
    let min: THREE.Line3 | undefined = undefined;
    const pairs = polyline.value.getRawSegments();
    if (pairs.length > 0) {
      const coords = polyline.value.getRawVertices();
      for (let i = 0, sz = pairs.length / 2; i < sz; ++i) {
        const j = i * 2;
        const k = pairs[j] * 3;
        const point = new THREE.Vector3().fromArray(coords, k);
        const res = ln.closestPointToPoint(point, true, new THREE.Vector3()).sub(point);
        const dist = res.length() - device.metadata.diameter / 2;
        if (dist <= 0) {
          min = new THREE.Line3(point, point);
          break;
        }
        res.setLength(dist).add(point);
        if (min === undefined || point.distanceTo(res) < min.distance()) {
          min = new THREE.Line3(point, res);
        }
      }
    }
    if (!min) {
      throw new Error('Undefined');
    }
    let orientationY = yVector.value.clone();
    let origin = min.end;
    if (min.distanceSq() > 0) {
      orientationY = min.delta(new THREE.Vector3()).normalize();
      if (orientationY.dot(yVector.value) < 0) {
        orientationY.negate();
        origin = min.start;
      }
    }
    const orientationZ = zVector.value.clone().cross(orientationY).negate().cross(orientationY).normalize();
    const orientationX = orientationY.clone().cross(orientationZ).normalize();
    const transformFrom = makeTransformation(origin, orientationX, orientationY, orientationZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(min.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(min.end.clone().applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(15, 15, 20));
    bBox.max.add(new THREE.Vector3(15, 15, 20));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    const location = {
      ptO: origin,
      dirY: orientationY,
      dirZ: orientationZ,
      minCorner: minCorner,
      maxCorner: maxCorner
    };
    return new DT.DistanceMeasurement(min.distance(), min, location);
  }
);

export const makePolylineFromPlanesTTVR = MakeFunctor(
  'FT.makePolylineFromPlanesTTVR',
  (
    rightAtrium: DT.MatTriangleSurface,
    ivc2Plane: DT.Plane,
    ivc1Plane: DT.Plane,
    svc1Plane: DT.Plane,
    svc2Plane?: DT.Plane
  ): DT.Polyline => {
    const accuracyMm = 0.25;

    const ivc2Params = sliceParams(
      _sliceInside(rightAtrium.value, ivc2Plane.value.planeEq.normal, ivc2Plane.value.origin)
    );
    const ivc1Params = sliceParams(
      _sliceInside(rightAtrium.value, ivc1Plane.value.planeEq.normal, ivc1Plane.value.origin)
    );
    const svc1Params = sliceParams(
      _sliceInside(rightAtrium.value, svc1Plane.value.planeEq.normal, svc1Plane.value.origin)
    );
    const svc2Params = svc2Plane
      ? sliceParams(_sliceInside(rightAtrium.value, svc2Plane.value.planeEq.normal, svc2Plane.value.origin))
      : sliceParams(
          _sliceInside(
            rightAtrium.value,
            svc1Plane.value.planeEq.normal,
            svc1Plane.value.origin.clone().addScaledVector(svc1Plane.value.planeEq.normal, 10)
          )
        );

    if (!ivc2Params || !ivc1Params || !svc1Params || !svc2Params) {
      throw new Error('Slicing failed');
    }

    if (ivc2Params.normal.dot(ivc1Params.center.clone().sub(ivc2Params.center)) < 0) {
      ivc2Params.normal.negate();
    }
    if (ivc1Params.normal.dot(svc1Params.center.clone().sub(ivc1Params.center)) < 0) {
      ivc1Params.normal.negate();
    }
    if (svc1Params.normal.dot(svc2Params.center.clone().sub(svc1Params.center)) < 0) {
      svc1Params.normal.negate();
    }
    if (svc2Params.normal.dot(svc2Params.center.clone().sub(svc1Params.center)) < 0) {
      svc2Params.normal.negate();
    }

    const ivc2ToIvc1 = _catmullRomSpline(
      ivc2Params.center,
      ivc2Params.normal,
      ivc1Params.center,
      ivc1Params.normal,
      accuracyMm
    );
    ivc2ToIvc1.pop();
    const ivc1ToSvc1 = _catmullRomSpline(
      ivc1Params.center,
      ivc1Params.normal,
      svc1Params.center,
      svc1Params.normal,
      accuracyMm
    );
    ivc1ToSvc1.pop();
    const svc1ToSvc2 = _catmullRomSpline(
      svc1Params.center,
      svc1Params.normal,
      svc2Params.center,
      svc2Params.normal,
      accuracyMm
    );
    svc1ToSvc2.pop();

    const splinePts = ivc2ToIvc1.concat(ivc1ToSvc1, svc1ToSvc2);

    // Result
    return new DT.Polyline({ points: splinePts, isClosed: false });
  }
);

export const heartOrientationTTVR = MakeFunctor(
  'FT.heartOrientationTTVR',
  (meas: DT.AreaMeasurement, vec: DT.Vector): DT.Orientation => {
    const vecTop = vec.value.clone().normalize();
    const vecPos = vec.value.clone().cross(meas.normal).normalize();
    return new DT.Orientation({ top: vecTop, dir: vecPos });
  }
);

export const makeCatheterTTVR = MakeFunctor(
  'FT.makeCatheterTTVR',
  (
    rightAtrium: DT.MatTriangleSurface,
    ivc2Plane: DT.Plane,
    ivc1Area: DT.AreaMeasurement,
    landingZone: DT.AreaMeasurement,
    diameterFr: DT.NumberT,
    factor1: DT.NumberT,
    factor2: DT.NumberT
  ): DT.Mesh => {
    const ivc2Params = sliceParams(
      _sliceInside(rightAtrium.value, ivc2Plane.value.planeEq.normal, ivc2Plane.value.origin)
    );

    if (!ivc2Params) {
      throw new Error('Slicing failed');
    }

    if (ivc2Params.normal.dot(ivc1Area.center.clone().sub(ivc2Params.center)) < 0) {
      ivc2Params.normal.negate();
    }

    const perp = getCommonPerpendicularLine(ivc1Area.center, ivc1Area.normal, landingZone.center, landingZone.normal);
    if (!perp) {
      throw new Error('Undefined');
    }

    const diameterMm = diameterFr.value / 3;
    const radiusMm = diameterMm / 2;

    const ptMid = ivc1Area.center.clone().add(landingZone.center).divideScalar(2);
    const ptCross = perp.start
      .clone()
      .sub(ptMid)
      .multiplyScalar(factor2.value / 100)
      .add(ptMid);

    const vec1 = ptCross.clone().sub(ivc1Area.center);
    vec1.setLength(((vec1.length() - radiusMm) * factor1.value) / 100);
    const ptMid1 = ivc1Area.center.clone().add(vec1);

    const vec2 = ptCross.clone().sub(landingZone.center);
    vec2.setLength(((vec2.length() - radiusMm) * factor1.value) / 100);
    const ptMid2 = landingZone.center.clone().add(vec2);

    const ptLast = landingZone.center.clone().sub(ptMid2).normalize().multiplyScalar(10).add(landingZone.center);

    const path = new THREE.CatmullRomCurve3(
      [ivc2Params.center, ivc1Area.center, ptMid1, ptMid2, landingZone.center, ptLast],
      false,
      CatmullRomCurve3Type.CatmullRom
    );
    const geometry = new THREE.TubeGeometry(path, 128, radiusMm, 32, false);

    return new DT.Mesh(geometry);
  }
);

export const ivcTvAngleTTVR_Deprecated = MakeFunctor(
  'FT.ivcTvAngleTTVR',
  (ivc1: DT.AreaMeasurement, landingZone: DT.AreaMeasurement): DT.AngleMeasurement => {
    const perp = getCommonPerpendicularLine(ivc1.center, ivc1.normal, landingZone.center, landingZone.normal);
    if (!perp) {
      throw new Error('Undefined');
    }
    const pt1 = perp.start.clone();
    const pt2 = landingZone.center.clone();
    const ptMid = ivc1.center.clone();
    const vec1 = pt1.clone().sub(ptMid);
    const vec2 = pt2.clone().sub(ptMid);
    //const pt2 = landingZone.center.clone();
    //const ptMid = ivc1.center.clone();
    //const vec2 = pt2.clone().sub(ptMid);
    //const vec1 = ivc1.normal.clone().setLength(vec2.length());
    //const pt1 = ptMid.clone().add(vec1);
    const angle = vec1.angleTo(vec2);
    const vecZ = vec1.clone().cross(vec2).normalize();
    const vecY = new THREE.Vector3(0, 0, -1);
    const vecX = vecY.clone().cross(vecZ).normalize();
    const transformFrom = makeTransformation(ptMid, vecX, vecY, vecZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(pt1.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt1).add(ptMid).applyMatrix4(transformTo));
    bBox.expandByPoint(pt2.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt2).add(ptMid).applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 0, 10));
    bBox.max.add(new THREE.Vector3(0, 0, 10));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    const location: DT.RegionOfInterestT = {
      ptO: ptMid.clone(),
      dirZ: vecZ,
      dirY: vecY,
      minCorner: minCorner,
      maxCorner: maxCorner
    };
    return new DT.AngleMeasurement(angle, ptMid, pt1, pt2, location);
  }
);

export const ivcTvAngle2TTVR_Deprecated = MakeFunctor(
  'FT.ivcTvAngle2TTVR',
  (ivc1: DT.AreaMeasurement, landingZone: DT.AreaMeasurement): DT.AngleMeasurement => {
    const perp = getCommonPerpendicularLine(ivc1.center, ivc1.normal, landingZone.center, landingZone.normal);
    if (!perp) {
      throw new Error('Undefined');
    }
    const ptMid = perp.start.clone();
    const pt1 = landingZone.center.clone();
    const pt2 = ivc1.center.clone();
    const vec1 = pt1.clone().sub(ptMid);
    const vec2 = pt2.clone().sub(ptMid);
    const angle = vec1.angleTo(vec2);
    const vecZ = vec1.clone().cross(vec2).normalize();
    const vecY = new THREE.Vector3(0, 0, -1);
    const vecX = vecY.clone().cross(vecZ).normalize();
    const transformFrom = makeTransformation(ptMid, vecX, vecY, vecZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(pt1.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt1).add(ptMid).applyMatrix4(transformTo));
    bBox.expandByPoint(pt2.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt2).add(ptMid).applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 0, 10));
    bBox.max.add(new THREE.Vector3(0, 0, 10));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    const location: DT.RegionOfInterestT = {
      ptO: ptMid.clone(),
      dirZ: vecZ,
      dirY: vecY,
      minCorner: minCorner,
      maxCorner: maxCorner
    };
    return new DT.AngleMeasurement(angle, ptMid, pt1, pt2, location);
  }
);

export const ivcTvAngle3TTVR_Deprecated = MakeFunctor(
  'FT.ivcTvAngle3TTVR',
  (ivc1: DT.AreaMeasurement, landingZone: DT.AreaMeasurement, factor: DT.NumberT): DT.AngleMeasurement => {
    const perp = getCommonPerpendicularLine(ivc1.center, ivc1.normal, landingZone.center, landingZone.normal);
    if (!perp) {
      throw new Error('Undefined');
    }
    const ptMid1 = ivc1.center.clone().add(landingZone.center).divideScalar(2);
    const ptMid = perp.start
      .clone()
      .sub(ptMid1)
      .multiplyScalar(factor.value / 100)
      .add(ptMid1);
    const pt1 = landingZone.center.clone();
    const pt2 = ivc1.center.clone();
    const vec1 = pt1.clone().sub(ptMid);
    const vec2 = pt2.clone().sub(ptMid);
    const angle = vec1.angleTo(vec2);
    const vecZ = vec1.clone().cross(vec2).normalize();
    const vecY = new THREE.Vector3(0, 0, -1);
    const vecX = vecY.clone().cross(vecZ).normalize();
    const transformFrom = makeTransformation(ptMid, vecX, vecY, vecZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(pt1.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt1).add(ptMid).applyMatrix4(transformTo));
    bBox.expandByPoint(pt2.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt2).add(ptMid).applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 0, 10));
    bBox.max.add(new THREE.Vector3(0, 0, 10));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    const location: DT.RegionOfInterestT = {
      ptO: ptMid.clone(),
      dirZ: vecZ,
      dirY: vecY,
      minCorner: minCorner,
      maxCorner: maxCorner
    };
    return new DT.AngleMeasurement(angle, ptMid, pt1, pt2, location);
  }
);

export const depthHeightPointTTVR = MakeFunctor(
  'FT.depthHeightPointTTVR',
  (tree: DT.MatKDTree3d, surf: DT.MatTriangleSurface, pl: DT.Plane, dir: DT.Vector): DT.Point => {
    const direction = pl.value.planeEq.normal.clone().normalize();
    if (direction.dot(dir.value) < 0) direction.negate();
    const origin = pl.value.origin.clone().addScaledVector(direction, 1000);
    const pt = _rayCast(tree.value, surf.value, origin, direction.clone().negate());
    if (!pt) throw new Error('RayCast failed');
    return new DT.Point(pt);
  }
);

export const makeRaHeightRvDepthRoiTTVR = MakeFunctor(
  'FT.makeRaHeightRvDepthRoiTTVR',
  (
    ptPrimary: DT.Point,
    ptSecondary: DT.Point,
    plAnnulus: DT.Plane,
    yVector: DT.Vector,
    xVector: DT.Vector
  ): DT.RegionOfInterest => {
    const location: DT.RegionOfInterestT = {
      ptO: plAnnulus.value.planeEq.projectPoint(ptPrimary.value, new THREE.Vector3()),
      dirZ: new THREE.Vector3(),
      dirY: new THREE.Vector3(),
      minCorner: new THREE.Vector3(),
      maxCorner: new THREE.Vector3()
    };
    const dirX = ptPrimary.value.clone().sub(location.ptO).normalize();
    if (dirX.dot(xVector.value) < 0) {
      dirX.negate();
    }
    location.dirZ.copy(dirX).cross(yVector.value).normalize();
    location.dirY.copy(location.dirZ).cross(dirX);
    const transformFrom = makeTransformation(location.ptO, dirX, location.dirY, location.dirZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(location.ptO.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptPrimary.value.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptSecondary.value.clone().applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 40, 40));
    bBox.max.add(new THREE.Vector3(0, 40, 40));
    location.minCorner.copy(bBox.min).applyMatrix4(transformFrom);
    location.maxCorner.copy(bBox.max).applyMatrix4(transformFrom);
    return new DT.RegionOfInterest(location);
  }
);

export const makeRaHeightRvDepthApexRoiTTVR = MakeFunctor(
  'FT.makeRaHeightRvDepthApexRoiTTVR',
  (
    ptPrimary: DT.Point,
    ptSecondary: DT.Point,
    plAnnulus: DT.Plane,
    yVector: DT.Vector,
    xVector: DT.Vector
  ): DT.RegionOfInterest => {
    const location: DT.RegionOfInterestT = {
      ptO: plAnnulus.value.origin.clone(),
      dirZ: new THREE.Vector3(),
      dirY: new THREE.Vector3(),
      minCorner: new THREE.Vector3(),
      maxCorner: new THREE.Vector3()
    };
    const dirX = ptPrimary.value.clone().sub(location.ptO).normalize();
    if (dirX.dot(xVector.value) < 0) {
      dirX.negate();
    }
    location.dirZ.copy(dirX).cross(yVector.value).normalize();
    location.dirY.copy(location.dirZ).cross(dirX);
    const transformFrom = makeTransformation(location.ptO, dirX, location.dirY, location.dirZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(location.ptO.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptPrimary.value.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptSecondary.value.clone().applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 40, 40));
    bBox.max.add(new THREE.Vector3(0, 40, 40));
    location.minCorner.copy(bBox.min).applyMatrix4(transformFrom);
    location.maxCorner.copy(bBox.max).applyMatrix4(transformFrom);
    return new DT.RegionOfInterest(location);
  }
);

export const getBasalSplineTTVR = MakeFunctor(
  'FT.getBasalSplineTTVR',
  (
    rvTree: DT.MatKDTree3d,
    rvSurf: DT.MatTriangleSurface,
    raTree: DT.MatKDTree3d,
    raSurf: DT.MatTriangleSurface,
    pl: DT.Plane,
    polyline: DT.Polyline
  ): DT.Polyline => {
    const _getBreakIns = (
      tree: MatSDKTypes.KDTree3d,
      surf: MatSDKTypes.TriangleSurface,
      origin: THREE.Vector3,
      direction: THREE.Vector3,
      gapClosingDistanceMm = 5
    ): Array<_rayCast2ResultItem> => {
      let rayCastInfos = _rayCast2(tree, surf, origin.clone().addScaledVector(direction, -100), direction);

      // Filter out noisy intersections (holes in surface)
      const gapsIds = new Set<number>();
      for (let i = 0; i < rayCastInfos.length; ) {
        const currInfo = rayCastInfos[i];
        const isCurrBreakOut = currInfo.breakKind == _rayCast2BreakKind.BreakOut;
        const isCurrLastOne = i == rayCastInfos.length - 1;
        if (isCurrBreakOut && !isCurrLastOne) {
          const nextInfo = rayCastInfos[i + 1];
          const isNextBreakIn = nextInfo.breakKind == _rayCast2BreakKind.BreakIn;
          if (isNextBreakIn) {
            if (nextInfo.distance - currInfo.distance < gapClosingDistanceMm) {
              gapsIds.add(i).add(i + 1);
            }
            i += 1;
          } else {
            console.warn('RayCast: intersections discrepancy detected (bad edges?)');
          }
        }
        i += 1;
      }
      if (gapsIds.size != 0) {
        rayCastInfos = rayCastInfos.filter((value, index) => !gapsIds.has(index));
      }

      // Filter nested intersections, Gather all BreakIn intersection points
      const surfPts = new Array<_rayCast2ResultItem>();
      if (rayCastInfos.length != 0) {
        let openedBreaksInCounter = 0;
        for (const info of rayCastInfos) {
          if (info.breakKind == _rayCast2BreakKind.BreakIn) {
            if (openedBreaksInCounter == 0) {
              surfPts.push(info);
            }
            ++openedBreaksInCounter;
          } else {
            --openedBreaksInCounter;
          }
        }
      }

      return surfPts;
    };

    const resPoints = new Array<THREE.Vector3>();
    const center = pl.value.origin;

    // For each original polyline point
    for (const point of polyline.value.points) {
      // Project polyline point on plane
      const projPt = pl.value.planeEq.projectPoint(point, new THREE.Vector3());

      // Find all points on surface via ray-casting
      const projPtToCenterDir = center.clone().sub(projPt);
      const projPtToCenterDist = projPtToCenterDir.length();
      projPtToCenterDir.normalize();

      const surfPts = new Array<_rayCast2ResultItem>()
        .concat(
          _getBreakIns(raTree.value, raSurf.value, center, projPtToCenterDir),
          _getBreakIns(rvTree.value, rvSurf.value, center, projPtToCenterDir)
        )
        .sort((a, b) => a.distance - b.distance);

      // Get resulting point
      if (surfPts.length == 0) {
        resPoints.push(projPt);
      } else {
        // Determine best BreakIn intersection
        let surfPt = surfPts[0].point;
        if (surfPts.length > 1) {
          let surfPtNeg = surfPt;
          let surfPtPos = surfPt;
          let minDistPos: number | undefined = undefined;
          let minDistNeg: number | undefined = undefined;
          for (const pt of surfPts) {
            const dir = center.clone().sub(pt.point);
            if (dir.dot(projPtToCenterDir) < 0) {
              break;
            }
            const dist = dir.length() - projPtToCenterDist;
            const distAbs = Math.abs(dist);
            if (dist < 0 && (minDistNeg === undefined || distAbs < minDistNeg)) {
              surfPtNeg = pt.point;
              minDistNeg = distAbs;
            }
            if (dist >= 0 && (minDistPos === undefined || distAbs < minDistPos)) {
              surfPtPos = pt.point;
              minDistPos = distAbs;
            }
          }
          if (minDistPos !== undefined) {
            if (minDistNeg !== undefined && minDistNeg < minDistPos && minDistNeg < 1) {
              surfPt = surfPtNeg;
            } else {
              surfPt = surfPtPos;
            }
          } else if (minDistNeg !== undefined) {
            surfPt = surfPtNeg;
          }
        }
        // Analyze intersection point and get the result
        const surfPtToCenterDir = center.clone().sub(surfPt);
        const surfPtToCenterDist = surfPtToCenterDir.length();
        if (surfPtToCenterDir.dot(projPtToCenterDir) < 0) {
          resPoints.push(projPt);
        } else if (surfPtToCenterDist < 10) {
          resPoints.push(center.clone().addScaledVector(projPtToCenterDir, -10));
        } else {
          resPoints.push(surfPt);
        }
      }
    }
    return new DT.Polyline({ points: resPoints, isClosed: true });
  }
);

export const makeBasalRvApLine = MakeFunctor('FT.makeBasalRvApLine', (meas: DT.AreaMeasurement): DT.Line => {
  const apLine = meas.lY;
  if (!apLine) {
    throw new Error('Undefined AP line');
  }
  return new DT.Line(new THREE.Line3(apLine.start.clone(), apLine.end.clone()));
});

export const makeBasalRvSlLine = MakeFunctor('FT.makeBasalRvSlLine', (meas: DT.AreaMeasurement): DT.Line => {
  const slLine = meas.lX;
  if (!slLine) {
    throw new Error('Undefined SL line');
  }
  return new DT.Line(new THREE.Line3(slLine.start.clone(), slLine.end.clone()));
});

export const makeBasalRvRoi = MakeFunctor(
  'FT.makeBasalRvRoi',
  (apLine: DT.Line, slLine: DT.Line, meas: DT.AreaMeasurement): DT.RegionOfInterest => {
    const origin = meas.center.clone();
    const vecZ = meas.normal.clone().normalize();
    const vecY = meas.orientationY?.clone().normalize() ?? apLine.value.delta(new THREE.Vector3()).normalize();
    const vecX = vecY.clone().cross(vecZ).normalize();
    const transformFrom = makeTransformation(origin, vecX, vecY, vecZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(slLine.value.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(slLine.value.end.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(apLine.value.start.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(apLine.value.end.clone().applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 0, 10));
    bBox.max.add(new THREE.Vector3(0, 0, 10));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    return new DT.RegionOfInterest({
      ptO: origin,
      dirZ: vecZ,
      dirY: vecY,
      minCorner: minCorner,
      maxCorner: maxCorner
    });
  }
);

export const getBasalRvSizeTTVR = MakeFunctor(
  'FT.getBasalRvSizeTTVR',
  (apDist: DT.DistanceMeasurement, slDist: DT.DistanceMeasurement, isApBased: DT.BooleanT): DT.NumberT => {
    const size = isApBased.value ? apDist.distance : slDist.distance;
    if (size === undefined) {
      throw new Error('Undefined size');
    }
    return new DT.NumberT(size);
  }
);

export const calculateOversizeTTVR = MakeFunctor(
  'FT.calculateOversizeTTVR',
  (deviceDiameter: DT.NumberT, anatomicalDiameter: DT.NumberT): DT.NumberT => {
    return new DT.NumberT((deviceDiameter.value - anatomicalDiameter.value) / anatomicalDiameter.value);
  }
);

export const makeCatheterTTVRNew = MakeFunctor(
  'FT.makeCatheterTTVRNew',
  (
    rightAtrium: DT.MatTriangleSurface,
    svc2Plane: DT.Plane,
    ivc2Plane: DT.Plane,
    landingZone: DT.AreaMeasurement,
    diameterFr: DT.NumberT
  ): DT.Mesh => {
    const svc2Params = sliceParams(
      _sliceInside(rightAtrium.value, svc2Plane.value.planeEq.normal, svc2Plane.value.origin)
    );
    if (!svc2Params) throw new Error('Slicing failed');

    const ivc2Params = sliceParams(
      _sliceInside(rightAtrium.value, ivc2Plane.value.planeEq.normal, ivc2Plane.value.origin)
    );
    if (!ivc2Params) throw new Error('Slicing failed');

    const dir0 = ivc2Params.center.clone().sub(svc2Params.center).normalize();

    const pt0 = getPlaneLineIntersectionPoint(
      landingZone.center,
      landingZone.normal.clone().cross(ivc2Plane.value.planeEq.normal).cross(landingZone.normal),
      ivc2Params.center,
      dir0
    );
    if (!pt0) throw new Error('No intersection');

    const pt01 = new THREE.Plane()
      .setFromNormalAndCoplanarPoint(landingZone.normal, pt0)
      .projectPoint(landingZone.center, new THREE.Vector3());

    const bottomStickingPartLength = 40;
    const frontStickingPartLength = 78;
    const middlePartLength = 35;
    const jointHalfLength = 10;

    const firstRotationDistanceSquared = pt0.distanceToSquared(pt01);
    const dist_01 = Math.sqrt(middlePartLength * middlePartLength - firstRotationDistanceSquared);

    const pt_0 = pt0.clone().addScaledVector(dir0, dist_01);
    const dir1 = pt01.clone().sub(pt_0).normalize();
    const dir2 = landingZone.center.clone().sub(pt01).normalize();

    const pt_b1 = pt_0.clone().addScaledVector(dir0, jointHalfLength);
    const pt_b2 = pt_0.clone().addScaledVector(dir0, jointHalfLength + 0.1);
    const pt_b3 = pt_0.clone().addScaledVector(dir0, bottomStickingPartLength);
    const pt_f1 = pt_0.clone().addScaledVector(dir1, jointHalfLength);
    const pt_f2 = pt_0.clone().addScaledVector(dir1, jointHalfLength + 0.1);
    const pt_f3 = pt_0.clone().addScaledVector(dir1, middlePartLength - jointHalfLength - 0.1);
    const pt_f4 = pt_0.clone().addScaledVector(dir1, middlePartLength - jointHalfLength);
    const pt_f5 = pt01.clone().addScaledVector(dir2, jointHalfLength);
    const pt_f6 = pt01.clone().addScaledVector(dir2, jointHalfLength + 0.1);
    const pt_f7 = pt01.clone().addScaledVector(dir2, frontStickingPartLength);

    const diameterMm = diameterFr.value / 3;
    const radiusMm = diameterMm / 2;

    const path = new THREE.CatmullRomCurve3(
      [pt_b3, pt_b2, pt_b1, pt_f1, pt_f2, pt_f3, pt_f4, pt_f5, pt_f6, pt_f7],
      false,
      CatmullRomCurve3Type.Chordal
    );
    const geometry = new THREE.TubeGeometry(path, 256, radiusMm, 32, false);

    return new DT.Mesh(geometry);
  }
);

export const catheterReferenceCsTTVR = MakeFunctor(
  'FT.catheterReferenceCsTTVR',
  (
    topDir: DT.Vector,
    defaultCatheterDiameterFr: DT.NumberT,
    ivcArea: DT.AreaMeasurement,
    annulusArea: DT.AreaMeasurement
  ): DT.TransformMatrix => {
    // orientation
    const vecZ = topDir.value.clone();
    const vecDir = ivcArea.center.clone().sub(annulusArea.center);
    const vecY = (
      vecDir.dot(annulusArea.normal) > 0 ? annulusArea.normal.clone() : annulusArea.normal.clone().negate()
    ).cross(vecZ);
    const vecX = new THREE.Vector3().crossVectors(vecY, vecZ);
    const mtx = new THREE.Matrix4().makeBasis(vecX.normalize(), vecY.normalize(), vecZ.normalize());
    // position
    const params = areaMeasurementParams(ivcArea.basePts, true, 1, vecZ, vecDir, ivcArea.center);
    if (params?.lY) {
      const direction = params.lY.delta(new THREE.Vector3()).normalize();
      const origin = params.lY.end.clone();
      if (vecDir.dot(direction) < 0) {
        direction.negate();
        origin.copy(params.lY.start);
      }
      const defCatheterRadiusMm = defaultCatheterDiameterFr.value / 3 / 2;
      mtx.setPosition(origin.addScaledVector(direction, -defCatheterRadiusMm));
    } else {
      mtx.setPosition(ivcArea.center);
    }
    return new DT.TransformMatrix(mtx);
  }
);

export const catheterPositionTTVR = MakeFunctor(
  'FT.catheterPositionTTVR',
  (referenceCs: DT.TransformMatrix, zRotationDeg: DT.NumberT): DT.TransformMatrix => {
    const origin = new THREE.Vector3(0, 0, 0).applyMatrix4(referenceCs.value);
    const vecZ = new THREE.Vector3(0, 0, 1).transformDirection(referenceCs.value);
    const mtx = makeRotationMatrix(origin, vecZ, (zRotationDeg.value * Math.PI) / 180);
    return new DT.TransformMatrix(mtx);
  }
);

export const catheterDefaultParamsTTVR = MakeFunctor(
  'FT.catheterDefaultParamsTTVR',
  (
    referenceCs: DT.TransformMatrix,
    landingZone: DT.AreaMeasurement,
    interJointDistance: DT.NumberT,
    maxPrimaryRotationDeg: DT.NumberT
  ): DT.CatheterDefaultParams => {
    const origin = new THREE.Vector3().setFromMatrixPosition(referenceCs.value);
    const vecX = new THREE.Vector3();
    const vecY = new THREE.Vector3();
    const vecZ = new THREE.Vector3();
    referenceCs.value.extractBasis(vecX, vecY, vecZ);

    const interJointDist = interJointDistance.value;

    const rayXPt = landingZone.center.clone();
    const rayXDir = landingZone.normal.clone();
    if (rayXDir.dot(vecX) < 0) rayXDir.negate();
    const rayZPt = origin.clone();
    const rayZDir = vecZ.clone().negate();
    const rayXDirInv = rayXDir.clone().negate();

    const helperVector1 = new THREE.Vector3();
    const helperVector2 = new THREE.Vector3();
    const angleCalculator = (dir: THREE.Vector3): number => {
      const dir1 = helperVector1.crossVectors(dir, rayXDir);
      const dir2 = helperVector2.crossVectors(dir, rayZDir);
      return (dir1.angleTo(dir2) * 180) / Math.PI;
    };

    const commonPerpendicular = getCommonPerpendicularLine(rayZPt, rayZDir, rayXPt, rayXDir);
    if (!commonPerpendicular) throw new Error('Logic error'); // lines are parallel

    const maxZRayMovementSq = interJointDist * interJointDist - commonPerpendicular.distanceSq();

    let jointLine: THREE.Line3 | undefined = undefined;
    if (maxZRayMovementSq > 0) {
      // common perpendicular length is shorter or equal to interJointDist

      const roughStep = 0.1;
      const roughToPreciseIterCnt = 4; // preciseStep = roughStep / 1e(roughToPreciseIterCnt)

      const maxZRayMovement = Math.sqrt(maxZRayMovementSq);

      if (maxZRayMovement > roughStep) {
        const ln0 = makeRaysJointLine(rayZPt, rayZDir, rayXPt, rayXDir, interJointDist, maxZRayMovement);
        const ln1 = makeRaysJointLine(rayZPt, rayZDir, rayXPt, rayXDir, interJointDist, maxZRayMovement - roughStep);
        const ln2 = makeRaysJointLine(rayZPt, rayZDir, rayXPt, rayXDirInv, interJointDist, maxZRayMovement - roughStep);
        if (!ln0 || !ln1 || !ln2) throw new Error('Logic error');
        const angle0 = angleCalculator(ln0.delta(new THREE.Vector3()));
        const angle1 = angleCalculator(ln1.delta(new THREE.Vector3()));
        const angle2 = angleCalculator(ln2.delta(new THREE.Vector3()));
        let isOppositeDir = false;
        let wasGrowing = false;
        if (Math.abs(90 - angle1) <= Math.abs(90 - angle2)) {
          wasGrowing = angle1 > angle0;
          isOppositeDir = wasGrowing ? 90 < angle0 : 90 > angle0;
        } else {
          wasGrowing = angle2 > angle0;
          isOppositeDir = wasGrowing ? 90 > angle0 : 90 < angle0;
        }
        const rayXDirAdjusted = isOppositeDir ? rayXDirInv : rayXDir;

        let prevLn = ln0;
        let prevAngle = angle0;
        let found = false;
        const dummyVector = new THREE.Vector3();
        const maxCurvatureRad = (maxPrimaryRotationDeg.value * Math.PI) / 180;
        let i = 3; // TODO: [chebotarev] testing
        for (
          let iterCnt = roughToPreciseIterCnt, stp = roughStep, distZ = maxZRayMovement - stp;
          !found && distZ >= 0 && i < 1000;
          distZ -= stp, ++i
        ) {
          const ln = makeRaysJointLine(rayZPt, rayZDir, rayXPt, rayXDirAdjusted, interJointDist, distZ);
          if (!ln) continue;
          const dir = ln.delta(dummyVector);
          let candidateCaseNum = 0; // 0 - not found; 1 - OK(90deg); 2,3 - corner cases (90deg unachievable)
          if (dir.angleTo(rayXDir) > maxCurvatureRad) {
            // angle is more than allowed - candidate found, increase precision
            candidateCaseNum = 3;
          } else {
            const angle = angleCalculator(dir);
            const isGrowing = angle > prevAngle;
            if (prevAngle <= 90 != angle <= 90) {
              // jumped over the 90 degrees - candidate found, increase precision
              candidateCaseNum = 1;
            } else if (wasGrowing != isGrowing) {
              // 90 degrees unachievable - corner case, minimize difference - candidate found, increase precision
              candidateCaseNum = 2;
            } else {
              // search further with current precision
              prevLn = ln;
              prevAngle = angle;
              wasGrowing = isGrowing;
            }
          }
          if (candidateCaseNum > 0) {
            jointLine = prevLn;
            if (--iterCnt > 0) {
              // increase precision
              distZ += stp;
              stp /= 10;
            } else {
              // done, stop the loop
              found = true;
            }
          }
        }
        if (!found) {
          jointLine = ln0;
        }
      }
    }

    const lineZ = new THREE.Line3(rayZPt, rayZPt.clone().add(rayZDir));

    if (jointLine) {
      const jointVector = jointLine.delta(new THREE.Vector3());

      const vecXRotated = vecZ.clone().cross(jointVector);
      if (vecXRotated.dot(vecX) < 0) {
        vecXRotated.negate();
      }

      let zRotationAngleRad = vecX.angleTo(vecXRotated);
      if (vecX.clone().cross(vecXRotated).dot(vecZ) < 0) {
        zRotationAngleRad = -zRotationAngleRad;
      }

      const primaryRotationAngleRad = jointVector.angleTo(rayXDir);
      let secondaryRotationAngleRad = vecZ.angleTo(jointVector);
      if (jointVector.dot(vecY) > 0) {
        secondaryRotationAngleRad = -secondaryRotationAngleRad;
      }

      const insertionVector = new THREE.Vector3().subVectors(jointLine.start, origin);

      // Align tip entry point with landingZone's center
      const tipRotationAxis = jointVector.clone().cross(vecXRotated).normalize();
      const tipRotationMtx = new THREE.Matrix4().makeRotationAxis(tipRotationAxis, primaryRotationAngleRad);
      const tipVector = jointVector.clone().transformDirection(tipRotationMtx);
      const intersectionPt = getPlaneLineIntersectionPoint(rayXPt, rayXDir, jointLine.end, tipVector);
      if (intersectionPt && intersectionPt.distanceToSquared(rayXPt) > 1e-2) {
        // adjust insertion depth
        const rayXPtProj = lineZ.closestPointToPoint(rayXPt, false, new THREE.Vector3());
        const intersectionPtProj = lineZ.closestPointToPoint(intersectionPt, false, new THREE.Vector3());
        const insertionVectorAdj = rayXPtProj.clone().sub(intersectionPtProj);
        if (insertionVectorAdj.lengthSq() > 1e-2) {
          insertionVector.add(insertionVectorAdj);
        }
        // adjust z-rotation
        const rayXPtDir = rayXPt.clone().sub(rayXPtProj);
        const intersectionPtDir = intersectionPt.clone().sub(intersectionPtProj);
        let zRotationAngleRadAdj = intersectionPtDir.angleTo(rayXPtDir);
        if (zRotationAngleRadAdj > 1e-4) {
          if (intersectionPtDir.clone().cross(rayXPtDir).dot(vecZ) < 0) {
            zRotationAngleRadAdj = -zRotationAngleRadAdj;
          }
          zRotationAngleRad += zRotationAngleRadAdj;
        }
      }

      let insertionDepth = insertionVector.length();
      if (insertionVector.dot(vecZ) < 0) {
        insertionDepth = -insertionDepth;
      }

      return new DT.CatheterDefaultParams({
        insertionDepth: insertionDepth,
        primaryRotationAngleDeg: (primaryRotationAngleRad * 180) / Math.PI,
        secondaryRotationAngleDeg: (secondaryRotationAngleRad * 180) / Math.PI,
        zRotationAngleDeg: (zRotationAngleRad * 180) / Math.PI
      });
    } else {
      const perpVector = commonPerpendicular.delta(new THREE.Vector3());
      const isLeftRightSwapped = perpVector.dot(vecY) > 0;

      const jointStart = lineZ.closestPointToPoint(rayXPt, false, new THREE.Vector3());
      const rotationRad = Math.acos(interJointDist / jointStart.distanceTo(rayXPt));
      const rotationMtx = new THREE.Matrix4().makeRotationAxis(
        rayZDir,
        isLeftRightSwapped ? -rotationRad : rotationRad
      );
      const jointVector = rayXPt.clone().sub(jointStart).transformDirection(rotationMtx);

      const vecXRotated = vecZ.clone().cross(jointVector);
      if (isLeftRightSwapped) {
        vecXRotated.negate();
      }
      let zRotationAngleRad = vecX.angleTo(vecXRotated);
      if (vecXRotated.dot(vecY) < 0) {
        zRotationAngleRad = -zRotationAngleRad;
      }

      const insertionVector = new THREE.Vector3().subVectors(jointStart, origin);
      let insertionDepth = insertionVector.length();
      if (insertionVector.dot(vecZ) < 0) {
        insertionDepth = -insertionDepth;
      }

      return new DT.CatheterDefaultParams({
        insertionDepth: insertionDepth,
        primaryRotationAngleDeg: 90,
        secondaryRotationAngleDeg: isLeftRightSwapped ? -90 : 90,
        zRotationAngleDeg: (zRotationAngleRad * 180) / Math.PI
      });
    }
  }
);

export const makeCatheterPathTTVR = MakeFunctor(
  'FT.makeCatheterPathTTVR',
  (
    mesh: DT.Mesh,
    referenceCs: DT.TransformMatrix,
    primaryRotationAngleDeg: DT.NumberT,
    secondaryRotationAngleDeg: DT.NumberT,
    zTranslation: DT.NumberT,
    interJointDistance: DT.NumberT,
    tipLength: DT.NumberT,
    jointCurvatureRadius: DT.NumberT
  ): DT.CatheterPath => {
    const ptSecondaryJoint = new THREE.Vector3(0, 0, 0);

    const secondaryRotation = new THREE.Matrix4().makeRotationX((secondaryRotationAngleDeg.value * Math.PI) / 180);
    const jointVector = new THREE.Vector3(0, 0, 1).transformDirection(secondaryRotation);

    const ptPrimaryJoint = jointVector.clone().setLength(interJointDistance.value);

    const primaryRotation = new THREE.Matrix4().makeRotationAxis(
      new THREE.Vector3(0, 1, 0).transformDirection(secondaryRotation),
      (primaryRotationAngleDeg.value * Math.PI) / 180
    );
    const primaryVector = jointVector.clone().transformDirection(primaryRotation).normalize();

    const ptTip = ptPrimaryJoint.clone().addScaledVector(primaryVector, tipLength.value);

    const mtx = new THREE.Matrix4().makeTranslation(0, 0, zTranslation.value);
    mtx.premultiply(referenceCs.value);

    ptSecondaryJoint.applyMatrix4(mtx);
    ptPrimaryJoint.applyMatrix4(mtx);
    ptTip.applyMatrix4(mtx);
    const vecBase = new THREE.Vector3(0, 0, -1).transformDirection(mtx);

    const ptBase = ptSecondaryJoint.clone().addScaledVector(vecBase, jointCurvatureRadius.value + 1);
    if (!mesh.value.boundingBox) {
      mesh.value.computeBoundingBox();
    }
    if (mesh.value.boundingBox) {
      const intersectionPt = getPlaneLineIntersectionPoint(
        mesh.value.boundingBox.min,
        new THREE.Vector3(0, 0, 1),
        ptBase,
        vecBase
      );
      if (intersectionPt && intersectionPt.clone().sub(ptBase).dot(vecBase) > 0) {
        ptBase.copy(intersectionPt);
      }
    }

    return new DT.CatheterPath({
      ptBase: ptBase,
      ptSecondaryJoint: ptSecondaryJoint,
      ptPrimaryJoint: ptPrimaryJoint,
      ptTip: ptTip
    });
  }
);

export const makeCatheterModelTTVR = MakeFunctor(
  'FT.makeCatheterModelTTVR',
  (path: DT.CatheterPath, diameterFr: DT.NumberT, jointCurvatureRadius: DT.NumberT): DT.Mesh => {
    const diameterMm = diameterFr.value / 3;
    const radiusMm = diameterMm / 2;

    const jointRadius = jointCurvatureRadius.value;

    const ptBase = path.value.ptBase;
    const ptSecondaryJoint = path.value.ptSecondaryJoint;
    const ptPrimaryJoint = path.value.ptPrimaryJoint;
    const ptTip = path.value.ptTip;

    const baseVector = ptSecondaryJoint.clone().sub(ptBase).normalize();
    const jointVector = ptPrimaryJoint.clone().sub(ptSecondaryJoint).normalize();
    const primaryVector = ptTip.clone().sub(ptPrimaryJoint).normalize();

    const pts = new Array<THREE.Vector3>();

    pts.push(ptBase.clone());
    pts.push(ptSecondaryJoint.clone().addScaledVector(baseVector, -jointRadius - 0.1));
    pts.push(ptSecondaryJoint.clone().addScaledVector(baseVector, -jointRadius));

    pts.push(ptSecondaryJoint.clone().addScaledVector(jointVector, jointRadius));
    pts.push(ptSecondaryJoint.clone().addScaledVector(jointVector, jointRadius + 0.1));
    pts.push(ptSecondaryJoint.clone().add(ptPrimaryJoint).divideScalar(2));
    pts.push(ptPrimaryJoint.clone().addScaledVector(jointVector, -jointRadius - 0.1));
    pts.push(ptPrimaryJoint.clone().addScaledVector(jointVector, -jointRadius));

    pts.push(ptPrimaryJoint.clone().addScaledVector(primaryVector, jointRadius));
    pts.push(ptPrimaryJoint.clone().addScaledVector(primaryVector, jointRadius + 0.1));
    pts.push(ptTip.clone());

    const curve = new THREE.CatmullRomCurve3(pts, false, CatmullRomCurve3Type.Chordal);

    const geometry = new THREE.TubeGeometry(curve, 128, radiusMm, 32, false);

    const coneHeight = diameterMm * 2;
    const cone = new THREE.ConeGeometry(radiusMm, coneHeight, 32, undefined, true);
    const coneOrigin = pts[pts.length - 1].clone().addScaledVector(primaryVector, coneHeight / 2);
    cone
      .applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), primaryVector))
      .translate(coneOrigin.x, coneOrigin.y, coneOrigin.z);

    const circle = new THREE.CircleGeometry(radiusMm, 32);
    const circleOrigin = pts[0];
    circle
      .applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, -1), baseVector))
      .translate(circleOrigin.x, circleOrigin.y, circleOrigin.z);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    return new DT.Mesh(mergeGeometries([geometry, cone, circle]));
  }
);

export const catheterZRotationAngleDegTTVR = MakeFunctor(
  'FT.catheterZRotationAngleDegTTVR',
  (params: DT.CatheterDefaultParams): DT.NumberT => {
    return new DT.NumberT(params.value.zRotationAngleDeg);
  }
);

export const catheterPrimaryRotationAngleDegTTVR = MakeFunctor(
  'FT.catheterPrimaryRotationAngleDegTTVR',
  (params: DT.CatheterDefaultParams): DT.NumberT => {
    return new DT.NumberT(params.value.primaryRotationAngleDeg);
  }
);

export const catheterSecondaryRotationAngleDegTTVR = MakeFunctor(
  'FT.catheterSecondaryRotationAngleDegTTVR',
  (params: DT.CatheterDefaultParams): DT.NumberT => {
    return new DT.NumberT(params.value.secondaryRotationAngleDeg);
  }
);

export const catheterInsertionDepthTTVR = MakeFunctor(
  'FT.catheterInsertionDepthTTVR',
  (params: DT.CatheterDefaultParams): DT.NumberT => {
    return new DT.NumberT(params.value.insertionDepth);
  }
);

export const catheterRotationAngleTTVR = MakeFunctor(
  'FT.catheterRotationAngleTTVR',
  (cs: DT.TransformMatrix, refCs: DT.TransformMatrix): DT.AngleMeasurement => {
    const refX = new THREE.Vector3(1, 0, 0).transformDirection(refCs.value);
    const refY = new THREE.Vector3(0, 1, 0).transformDirection(refCs.value);
    const refZ = new THREE.Vector3(0, 0, 1).transformDirection(refCs.value);

    const vecX = new THREE.Vector3(1, 0, 0).transformDirection(refCs.value).transformDirection(cs.value);
    const projX = refZ.clone().cross(vecX).cross(refZ).normalize();

    const angle = projX.dot(refY) >= 0 ? refX.angleTo(vecX) : -refX.angleTo(vecX);

    const ptMid = new THREE.Vector3(0, 0, 0).applyMatrix4(refCs.value).applyMatrix4(cs.value);
    const pt1 = ptMid.clone().addScaledVector(refX, 20);
    const pt2 = ptMid.clone().addScaledVector(projX, 20);

    const transformFrom = makeTransformation(ptMid, refX, refY, refZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(pt1.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt1).add(ptMid).applyMatrix4(transformTo));
    bBox.expandByPoint(pt2.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt2).add(ptMid).applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 0, 35));
    bBox.max.add(new THREE.Vector3(0, 0, 35));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    const location: DT.RegionOfInterestT = {
      ptO: ptMid.clone(),
      dirZ: refZ,
      dirY: refY,
      minCorner: minCorner,
      maxCorner: maxCorner
    };
    return new DT.AngleMeasurement(angle, ptMid, pt1, pt2, location);
  }
);

export const catheterSecondaryFlexTTVR = MakeFunctor(
  'FT.catheterSecondaryBendTTVR',
  (path: DT.CatheterPath, cs: DT.TransformMatrix, refCs: DT.TransformMatrix): DT.AngleMeasurement => {
    const refX = new THREE.Vector3(1, 0, 0).transformDirection(refCs.value).transformDirection(cs.value);
    const refY = new THREE.Vector3(0, 1, 0).transformDirection(refCs.value).transformDirection(cs.value);
    const refZ = new THREE.Vector3(0, 0, 1).transformDirection(refCs.value).transformDirection(cs.value);

    const vecZ = path.value.ptPrimaryJoint
      .clone()
      .sub(path.value.ptSecondaryJoint)
      .transformDirection(cs.value)
      .normalize();

    const angle = vecZ.dot(refY) <= 0 ? refZ.angleTo(vecZ) : -refZ.angleTo(vecZ);

    const ptMid = path.value.ptSecondaryJoint.clone().applyMatrix4(cs.value);
    const pt1 = ptMid.clone().addScaledVector(refZ, 35);
    const pt2 = ptMid.clone().addScaledVector(vecZ, 35);

    const transformFrom = makeTransformation(ptMid, refX, refY, refZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(pt1.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt1).add(ptMid).applyMatrix4(transformTo));
    bBox.expandByPoint(pt2.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt2).add(ptMid).applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(20, 0, 0));
    bBox.max.add(new THREE.Vector3(20, 0, 0));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    const location: DT.RegionOfInterestT = {
      ptO: ptMid.clone(),
      dirZ: refX,
      dirY: refZ,
      minCorner: minCorner,
      maxCorner: maxCorner
    };
    return new DT.AngleMeasurement(angle, ptMid, pt1, pt2, location);
  }
);

export const catheterPrimaryFlexTTVR = MakeFunctor(
  'FT.catheterPrimaryBendTTVR',
  (path: DT.CatheterPath, cs: DT.TransformMatrix, refCs: DT.TransformMatrix): DT.AngleMeasurement => {
    const refX = new THREE.Vector3(1, 0, 0).transformDirection(refCs.value).transformDirection(cs.value);
    const refZ = path.value.ptPrimaryJoint
      .clone()
      .sub(path.value.ptSecondaryJoint)
      .transformDirection(cs.value)
      .normalize();
    const refY = refZ.clone().cross(refX).normalize();

    const vecZ = path.value.ptTip.clone().sub(path.value.ptPrimaryJoint).normalize().transformDirection(cs.value);

    const angle = vecZ.dot(refX) >= 0 ? refZ.angleTo(vecZ) : -refZ.angleTo(vecZ);

    const ptMid = path.value.ptPrimaryJoint.clone().applyMatrix4(cs.value);
    const pt1 = ptMid.clone().addScaledVector(refZ, 35);
    const pt2 = ptMid.clone().addScaledVector(vecZ, 35);

    const transformFrom = makeTransformation(ptMid, refX, refY, refZ);
    const transformTo = transformFrom.clone().invert();
    const bBox = new THREE.Box3();
    bBox.expandByPoint(pt1.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt1).add(ptMid).applyMatrix4(transformTo));
    bBox.expandByPoint(pt2.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptMid.clone().sub(pt2).add(ptMid).applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 20, 0));
    bBox.max.add(new THREE.Vector3(0, 20, 0));
    const minCorner = bBox.min.applyMatrix4(transformFrom);
    const maxCorner = bBox.max.applyMatrix4(transformFrom);
    const location: DT.RegionOfInterestT = {
      ptO: ptMid.clone(),
      dirZ: refY.negate(),
      dirY: refZ,
      minCorner: minCorner,
      maxCorner: maxCorner
    };
    return new DT.AngleMeasurement(angle, ptMid, pt1, pt2, location);
  }
);

export const catheterToTVNormalAngleTTVR = MakeFunctor(
  'FT.catheterToTVNormalAngleTTVR',
  (path: DT.CatheterPath, cs: DT.TransformMatrix, landingZone: DT.AreaMeasurement): DT.AngleMeasurement => {
    const vecDir = path.value.ptSecondaryJoint.clone().applyMatrix4(cs.value).sub(landingZone.center);
    const vecDesired =
      vecDir.dot(landingZone.normal) > 0 ? landingZone.normal.clone() : landingZone.normal.clone().negate();
    vecDesired.normalize();

    const ptPrimaryJoint = path.value.ptPrimaryJoint.clone().applyMatrix4(cs.value);
    const ptTip = path.value.ptTip.clone().applyMatrix4(cs.value);

    const vecActual = ptPrimaryJoint.clone().sub(ptTip);
    vecActual.normalize();

    const angle = vecDesired.angleTo(vecActual);

    let ptMid = getPlaneLineIntersectionPoint(landingZone.center, landingZone.normal, ptPrimaryJoint, vecActual);
    if (!ptMid) {
      ptMid = landingZone.center;
    } else if (ptMid.distanceToSquared(ptPrimaryJoint) > vecActual.lengthSq()) {
      ptMid = ptTip;
    } else if (ptMid.distanceToSquared(ptTip) > vecActual.lengthSq()) {
      ptMid = ptPrimaryJoint;
    }
    const pt1 = ptMid.clone().addScaledVector(vecDesired, 35);
    const pt2 = ptMid.clone().addScaledVector(vecActual, 35);

    return new DT.AngleMeasurement(angle, ptMid, pt1, pt2);
  }
);

export const volumeOfRightAtriumTTVR = MakeFunctor(
  'FT.volumeOfRightAtriumTTVR',
  (
    raTri: DT.MatTriangleSurface,
    ivc: DT.AreaMeasurement,
    svc: DT.AreaMeasurement,
    worldTopDir: DT.Vector
  ): DT.NumberT => {
    //const start = Date.now();
    const planes: Array<{ plO: THREE.Vector3; plN: THREE.Vector3; plEq: THREE.Plane }> = [
      {
        plO: ivc.center,
        plN: ivc.normal.dot(worldTopDir.value) <= 0 ? ivc.normal : ivc.normal.clone().negate(),
        plEq: new THREE.Plane().setFromNormalAndCoplanarPoint(ivc.normal, ivc.center)
      },
      {
        plO: svc.center,
        plN: svc.normal.dot(worldTopDir.value) >= 0 ? svc.normal : svc.normal.clone().negate(),
        plEq: new THREE.Plane().setFromNormalAndCoplanarPoint(svc.normal, svc.center)
      }
    ];

    let volume = raTri.value.getVolume();

    const pt = new THREE.Vector3();

    for (const plane of planes) {
      const vol = emmanaged2(new MatSDK.Double3(), new MatSDK.Double3(), (normal, origin) => {
        normal.setFromArray(plane.plN.toArray());
        origin.setFromArray(plane.plO.toArray());
        return emmanaged(MatSDK.cutWithPlaneAbove(raTri.value, origin, normal, true), (aboveModel) => {
          return emmanaged(MatSDK.splitShells(aboveModel, MatSDK.ShellsConnectivity.Edge), (shells) => {
            const sz = shells.size();
            if (sz == 0) return 0;
            if (sz == 1) return shells.access(0).getVolume();
            let minDist = Number.POSITIVE_INFINITY;
            let idx = 0;
            for (let i = 0; i < sz; ++i) {
              const shell = shells.access(i);
              const vert = shell.getRawVertices();
              pt.fromArray(vert, vert.length - 3); // last point is expected to be the part of the planar patch
              if (plane.plEq.distanceToPoint(pt) > 0.0001) {
                // if not the point on plane, search for it:
                for (let i = 0, sz = vert.length - 3; i < sz; i += 3) {
                  pt.fromArray(vert, i);
                  if (plane.plEq.distanceToPoint(pt) < 0.0001) {
                    break;
                  }
                }
              }
              const dist = pt.distanceToSquared(plane.plO);
              if (dist < minDist) {
                minDist = dist;
                idx = i;
              }
            }
            return shells.access(idx).getVolume();
          });
        });
      });
      volume -= vol;
    }
    return new DT.NumberT(volume);
  }
);

export const findAnnulusQuarterIndexTTVR = MakeFunctor(
  'FT.findAnnulusQuarterIndexTTVR',
  (pt: DT.Point, area: DT.AreaMeasurement, apDir: DT.Vector): DT.NumberT => {
    const planeCs = makePlaneCoordinateSystem(area.normal, area.center);

    let quarterIdx = -1; // Invalid - non concentric
    if (isPointInsideSpline2d(pt.value, area.splinePts, planeCs.clone().invert())) {
      const planeEq = new THREE.Plane().setFromNormalAndCoplanarPoint(area.normal, area.center);
      const proj = planeEq.projectPoint(pt.value, new THREE.Vector3());
      const vec = proj.clone().sub(area.center);

      const vecY = apDir.value.clone().cross(planeEq.normal).negate().cross(planeEq.normal).normalize();

      const angleRad = vecY.angleTo(vec);
      if (vecY.cross(vec).dot(planeEq.normal) > 0) {
        if (angleRad <= Math.PI / 2) {
          quarterIdx = 2; // Q2
        } else {
          quarterIdx = 3; // Q3
        }
      } else {
        if (angleRad <= Math.PI / 2) {
          quarterIdx = 1; // Q1
        } else {
          quarterIdx = 4; // Q4
        }
      }
    }

    return new DT.NumberT(quarterIdx);
  }
);

export const countUniquePapillaryQuartersTTVR = MakeFunctor(
  'FT.countUniquePapillaryQuartersTTVR',
  (...quarterIndices: Array<DT.NumberT>): DT.NumberT => {
    const uniqueIndices = new Set(quarterIndices.map((i) => i.value).filter((v) => v != -1)); // Filter out non-concentric
    return new DT.NumberT(uniqueIndices.size);
  }
);

export const countPapillaryHeightsTTVR = MakeFunctor(
  'FT.countPapillaryHeightsTTVR',
  (area: DT.AreaMeasurement, maxLen: DT.NumberT, ...heights: Array<DT.DistanceMeasurement>): DT.NumberT => {
    const concentricPoints = filterPointsInsideSpline2d(
      heights.filter((h) => h.distance < maxLen.value).map((h) => h.line.start),
      area.splinePts,
      makePlaneCoordinateSystem(area.normal, area.center).invert()
    );
    return new DT.NumberT(concentricPoints.length);
  }
);

export const ivcToTvaDistanceTTVR = MakeFunctor(
  'FT.ivcToTvaDistanceTTVR',
  (ivcArea: DT.AreaMeasurement, tvaArea: DT.AreaMeasurement): DT.DistanceMeasurement => {
    const topDirection = new THREE.Vector3(0, 0, 1);

    const orientationZ = topDirection.clone().normalize();
    const orientationX = orientationZ.clone().cross(tvaArea.normal).cross(orientationZ).normalize();
    if (orientationX.dot(tvaArea.center.clone().sub(ivcArea.center)) < 0) orientationX.negate();
    const orientationY = orientationZ.clone().cross(orientationX).normalize();
    const transformTo = makeTransformation(ivcArea.center, orientationX, orientationY, orientationZ).invert();

    let minPtIdx = -1;
    let minDist = Number.MAX_SAFE_INTEGER;
    for (let i = 0, sz = ivcArea.splinePts.length; i < sz; ++i) {
      const dist = ivcArea.splinePts[i].clone().applyMatrix4(transformTo).x;
      if (dist < minDist) {
        minPtIdx = i;
        minDist = dist;
      }
    }

    if (minPtIdx < 0) {
      throw new Error('IVC to TVA distance not found');
    }

    const ptMin = ivcArea.splinePts[minPtIdx];

    const ptFrom = getPlaneLineIntersectionPoint(
      ptMin,
      topDirection.clone().cross(tvaArea.normal).cross(topDirection),
      tvaArea.center,
      tvaArea.normal
    );

    if (!ptFrom) {
      throw new Error('No intersection');
    }

    const ptTo = tvaArea.center.clone();

    const line = new THREE.Line3(ptFrom, ptTo);

    const location: DT.RegionOfInterestT = {
      ptO: ptFrom.clone(),
      dirZ: new THREE.Vector3(),
      dirY: new THREE.Vector3(),
      minCorner: new THREE.Vector3(),
      maxCorner: new THREE.Vector3()
    };

    const dirX = line.delta(new THREE.Vector3()).normalize();
    location.dirY.copy(dirX).cross(orientationY).normalize();
    location.dirZ.copy(dirX).cross(location.dirY).normalize();

    const transformFrom = makeTransformation(location.ptO, dirX, location.dirY, location.dirZ);
    transformTo.copy(transformFrom).invert();

    const bBox = new THREE.Box3();
    bBox.expandByPoint(ptFrom.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(ptTo.clone().sub(ptFrom).multiplyScalar(2).add(ptFrom).applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 40, 40));
    bBox.max.add(new THREE.Vector3(0, 40, 40));
    location.minCorner.copy(bBox.min).applyMatrix4(transformFrom);
    location.maxCorner.copy(bBox.max).applyMatrix4(transformFrom);

    return new DT.DistanceMeasurement(line.distance(), line, location);
  }
);

export const ivcToTvaOffsetDistanceTTVR = MakeFunctor(
  'FT.ivcToTvaOffsetDistanceTTVR',
  (
    ivcArea: DT.AreaMeasurement,
    tvaArea: DT.AreaMeasurement,
    yesRange: DT.CriteriaRange,
    noRange: DT.CriteriaRange
  ): DT.DistanceMeasurement => {
    const topDirection = new THREE.Vector3(0, 0, 1);
    const ivcToTvaDir = tvaArea.center.clone().sub(ivcArea.center);
    const rayOrigin = tvaArea.center.clone();
    const rayDirection = tvaArea.normal.clone();
    if (rayDirection.dot(ivcToTvaDir) < 0) rayDirection.negate();
    const res = getSplineDistanceToRayProjected(
      ivcArea.splinePts,
      new THREE.Ray(rayOrigin, rayDirection),
      topDirection
    );

    const ptRes = res.pointIndex >= 0 ? ivcArea.splinePts[res.pointIndex] : ivcArea.center;

    const ptFrom = getPlaneLineIntersectionPoint(
      ptRes,
      topDirection.clone().cross(tvaArea.normal).cross(topDirection),
      tvaArea.center,
      tvaArea.normal
    );

    if (!ptFrom) {
      throw new Error('No intersection');
    }

    const ptTo =
      res.pointIndex >= 0
        ? new THREE.Plane().setFromNormalAndCoplanarPoint(topDirection, ptFrom).projectPoint(ptRes, new THREE.Vector3())
        : ptFrom.clone();

    // result
    const line = new THREE.Line3(ptFrom, ptTo);

    const location: DT.RegionOfInterestT = {
      ptO: ptFrom.clone(),
      dirZ: new THREE.Vector3(),
      dirY: new THREE.Vector3(),
      minCorner: new THREE.Vector3(),
      maxCorner: new THREE.Vector3()
    };
    const dirX = rayDirection.clone().negate().normalize();
    location.dirY.copy(topDirection).cross(dirX).normalize();
    location.dirZ.copy(dirX).cross(location.dirY).normalize();
    const transformFrom = makeTransformation(location.ptO, dirX, location.dirY, location.dirZ);
    const transformTo = transformFrom.clone().invert();

    const bBox = new THREE.Box3();
    bBox.expandByPoint(ptFrom.clone().applyMatrix4(transformTo));
    bBox.expandByPoint(tvaArea.center.clone().sub(ptFrom).multiplyScalar(2).add(ptFrom).applyMatrix4(transformTo));
    bBox.min.sub(new THREE.Vector3(0, 40, 40));
    bBox.max.add(new THREE.Vector3(0, 40, 40));
    location.minCorner = bBox.min.applyMatrix4(transformFrom);
    location.maxCorner = bBox.max.applyMatrix4(transformFrom);

    return new DT.DistanceMeasurement(
      res.distance,
      line,
      location,
      _makeCheckCriteria(res.distance, DT.CheckStatusT.Maybe, yesRange.value, undefined, noRange.value)
    );
  }
);

export const findApexPointTTVR = MakeFunctor(
  'FT.findApexPointTTVR',
  (tree: DT.MatKDTree3d, surf: DT.MatTriangleSurface, pl: DT.Plane, apDir: DT.Vector, dir: DT.Vector): DT.Point => {
    const direction = pl.value.planeEq.normal.clone().normalize();
    if (direction.dot(dir.value) < 0) direction.negate();

    const rotationDir = apDir.value.clone().cross(direction).negate().cross(direction).normalize();
    const rotationMtx = new THREE.Matrix4().makeRotationAxis(rotationDir, Math.PI / 180);

    const rotationHalfRangeDeg = 60;
    let maxDistSq = Number.MIN_SAFE_INTEGER;
    let maxPoint: THREE.Vector3 | undefined = undefined;
    const origin = new THREE.Vector3();
    direction.transformDirection(
      new THREE.Matrix4().makeRotationAxis(rotationDir, (-rotationHalfRangeDeg * Math.PI) / 180)
    );
    for (let i = -rotationHalfRangeDeg; i <= rotationHalfRangeDeg; ++i) {
      origin.copy(pl.value.origin).addScaledVector(direction, 1000);
      const pt = _rayCast(tree.value, surf.value, origin, direction.clone().negate());
      if (pt) {
        const distSq = pl.value.origin.distanceToSquared(pt);
        if (distSq > maxDistSq) {
          maxPoint = pt;
          maxDistSq = distSq;
        }
      }
      direction.transformDirection(rotationMtx);
    }

    if (!maxPoint) throw new Error('RayCast failed');

    return new DT.Point(maxPoint);
  }
);

export const findMaxRaHeightPointTTVR = MakeFunctor(
  'FT.findMaxRaHeightPointTTVR',
  (tree: DT.MatKDTree3d, surf: DT.MatTriangleSurface, area: DT.AreaMeasurement, dir: DT.Vector): DT.Point => {
    const direction = area.normal.clone().normalize();
    if (direction.dot(dir.value) < 0) direction.negate();

    const ln = area.lX ?? new THREE.Line3(area.center, area.center);
    const cnt = Math.floor(ln.distance()) + 1;
    const vec = ln.delta(new THREE.Vector3());
    if (vec.lengthSq() > 1) vec.normalize();

    let maxDist = Number.MIN_SAFE_INTEGER;
    let maxPoint: THREE.Vector3 | undefined = undefined;
    const origin = new THREE.Vector3();
    for (let i = 0; i < cnt; ++i) {
      origin.copy(ln.start).addScaledVector(vec, i).addScaledVector(direction, 1000);
      const pt = _rayCast(tree.value, surf.value, origin, direction.clone().negate());
      if (!pt) continue;
      const dist = distancePointToPlane(pt, direction, area.center);
      if (dist > maxDist) {
        maxPoint = pt;
        maxDist = dist;
      }
    }

    if (!maxPoint) throw new Error('RayCast failed');

    return new DT.Point(maxPoint);
  }
);

export const makeLongAxisPolylineTTVR = MakeFunctor('FT.makeLongAxisPolylineTTVR', (plane: DT.Plane): DT.Polyline => {
  const accuracyMm = 0.25;
  const len = 30;

  const ptFrom = plane.value.origin.clone().addScaledVector(plane.value.planeEq.normal, -len);
  const ptTo = plane.value.origin.clone().addScaledVector(plane.value.planeEq.normal, len);
  const count = ptFrom.distanceTo(ptTo) / accuracyMm;

  const pt = ptFrom.clone();
  const vec = plane.value.planeEq.normal.clone().multiplyScalar(accuracyMm);
  const pts = new Array<THREE.Vector3>();
  for (let i = 0; i < count; ++i) {
    pts.push(pt.clone());
    pt.add(vec);
  }

  return new DT.Polyline({ points: pts, isClosed: false });
});
